import { API_URL as BASE_URL } from '../../lib/config';
const API_URL = `${BASE_URL}/platform-admin`;

export interface FeatureFlag {
  id: string;
  key: string;
  name: string;
  description: string | null;
  category: string;
  isGlobal: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    organizations: number;
  };
}

export interface OrganizationFeatureFlag {
  id: string;
  organizationId: string;
  featureFlagId: string;
  isEnabled: boolean;
  enabledAt: string;
  enabledBy: string | null;
  expiresAt: string | null;
  config: any;
  organization: {
    id: string;
    name: string;
    status: string;
  };
  featureFlag?: FeatureFlag;
}

export interface CreateFeatureFlagDto {
  key: string;
  name: string;
  description?: string;
  category: string;
  isGlobal?: boolean;
}

export interface UpdateFeatureFlagDto {
  name?: string;
  description?: string;
  category?: string;
  isGlobal?: boolean;
}

export interface EnableFeatureFlagDto {
  organizationIds: string[];
  expiresAt?: string;
  config?: any;
  enabledBy?: string;
}

export interface DisableFeatureFlagDto {
  organizationIds: string[];
}

export const featureFlagsService = {
  /**
   * Get all feature flags
   */
  async getAllFlags(token: string): Promise<FeatureFlag[]> {
    const response = await fetch(`${API_URL}/feature-flags`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch feature flags');
    }

    return response.json();
  },

  /**
   * Get feature flag by ID
   */
  async getFlagById(token: string, id: string): Promise<FeatureFlag> {
    const response = await fetch(`${API_URL}/feature-flags/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch feature flag');
    }

    return response.json();
  },

  /**
   * Create a new feature flag
   */
  async createFlag(token: string, data: CreateFeatureFlagDto): Promise<FeatureFlag> {
    const response = await fetch(`${API_URL}/feature-flags`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create feature flag');
    }

    return response.json();
  },

  /**
   * Update a feature flag
   */
  async updateFlag(
    token: string,
    id: string,
    data: UpdateFeatureFlagDto
  ): Promise<FeatureFlag> {
    const response = await fetch(`${API_URL}/feature-flags/${id}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update feature flag');
    }

    return response.json();
  },

  /**
   * Delete a feature flag
   */
  async deleteFlag(token: string, id: string): Promise<{ message: string }> {
    const response = await fetch(`${API_URL}/feature-flags/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to delete feature flag');
    }

    return response.json();
  },

  /**
   * Enable feature flag for organizations
   */
  async enableForOrganizations(
    token: string,
    flagId: string,
    data: EnableFeatureFlagDto
  ): Promise<{ message: string }> {
    const response = await fetch(`${API_URL}/feature-flags/${flagId}/enable`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to enable feature flag');
    }

    return response.json();
  },

  /**
   * Disable feature flag for organizations
   */
  async disableForOrganizations(
    token: string,
    flagId: string,
    data: DisableFeatureFlagDto
  ): Promise<{ message: string }> {
    const response = await fetch(`${API_URL}/feature-flags/${flagId}/disable`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to disable feature flag');
    }

    return response.json();
  },

  /**
   * Get organizations with a specific feature flag
   */
  async getOrganizationsWithFlag(
    token: string,
    flagId: string
  ): Promise<OrganizationFeatureFlag[]> {
    const response = await fetch(`${API_URL}/feature-flags/${flagId}/organizations`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch organizations');
    }

    return response.json();
  },

  /**
   * Get feature flags for a specific organization
   */
  async getFlagsForOrganization(
    token: string,
    organizationId: string
  ): Promise<FeatureFlag[]> {
    const response = await fetch(
      `${API_URL}/feature-flags/organizations/${organizationId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch organization feature flags');
    }

    return response.json();
  },

  /**
   * Check if feature is enabled for organization
   */
  async isFeatureEnabled(
    token: string,
    organizationId: string,
    flagKey: string
  ): Promise<boolean> {
    const response = await fetch(
      `${API_URL}/feature-flags/organizations/${organizationId}/features/${flagKey}/enabled`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to check feature status');
    }

    const data = await response.json();
    return data.enabled;
  },
};
